import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const APP_FR = { mot: "Mot à mot", phrase: "Phrase", construction: "Construction", brat: "Brat" };
const sc = (v) => (v > 0 ? "text-primary" : v < 0 ? "text-muted-foreground" : "text-foreground");

const Proposal = ({ p, onAdd, busy }) => (
  <div className="border border-border p-4 flex flex-col gap-3" data-testid={`effects-proposal-${p.style_id}`}>
    <div className="flex items-baseline justify-between gap-3">
      <b className="text-sm">{p.suggested_name}</b>
      <span className="font-osd text-xs text-muted-foreground">score {p.score}</span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {p.tags.map((t) => (
        <span key={t.tag} className="px-2 py-1 text-[11px] border border-primary/60 text-primary" title={t.tag}>{t.label} · +{t.score}</span>
      ))}
      {p.appearance && <span className="px-2 py-1 text-[11px] border border-border text-muted-foreground">{APP_FR[p.appearance] || p.appearance}</span>}
    </div>
    <p className="text-xs text-muted-foreground">Base : {p.name}. Les clips de banque et les sous-titres du style sont repris, avec ces effets appliqués.</p>
    <button type="button" disabled={busy || p.already} onClick={() => onAdd(p)} data-testid={`effects-proposal-add-${p.style_id}`}
      className="self-start px-4 py-2 text-xs font-osd tracking-wider border border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-40 transition-colors">
      {p.already ? "DÉJÀ AU CATALOGUE" : busy ? "AJOUT…" : "AJOUTER AU CATALOGUE"}
    </button>
  </div>
);

export const EffectsStatsAdmin = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/effects/stats"); setData(data); } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (p) => {
    setBusy(p.style_id);
    try {
      const { data: r } = await api.post("/admin/catalogue/remix", { style_id: p.style_id, tags: p.tags.map((t) => t.tag), appearance: p.appearance, name: p.suggested_name });
      toast.success(`« ${r.name} » ajouté au catalogue (${r.presets} sous-titres, ${r.clips} clips)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible"); }
    setBusy("");
  };
  const toggle = async (id) => {
    try { const { data: r } = await api.patch(`/admin/catalogue/remix/${id}`); toast.success(r.active ? "Remix réactivé" : "Remix masqué"); load(); } catch { toast.error("Impossible"); }
  };

  if (!data) return <p className="font-osd text-sm text-muted-foreground animate-pulse" data-testid="effects-stats-loading">CHARGEMENT…</p>;
  return (
    <div className="flex flex-col gap-8" data-testid="effects-stats-admin">
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-osd text-xs tracking-wider text-muted-foreground">STYLES À AJOUTER</h2>
          <span className="font-osd text-xs text-muted-foreground" data-testid="effects-stats-events">{data.events} gestes analysés</span>
        </div>
        {data.proposals.length ? (
          <div className="grid gap-3 md:grid-cols-2">{data.proposals.map((p) => <Proposal key={p.style_id} p={p} onAdd={add} busy={busy === p.style_id} />)}</div>
        ) : <p className="text-sm text-muted-foreground" data-testid="effects-proposals-empty">Pas encore assez de likes pour proposer un style. Les propositions apparaissent dès qu'un effet est liké ou téléchargé au moins deux fois dans un style.</p>}
      </section>
      <section>
        <h2 className="font-osd text-xs tracking-wider text-muted-foreground mb-3">EFFETS LES PLUS LIKÉS PAR STYLE</h2>
        <div className="flex flex-col divide-y divide-border border border-border" data-testid="effects-stats-list">
          {data.styles.map((s) => (
            <div key={s.style_id}>
              <button type="button" onClick={() => setOpen(open === s.style_id ? null : s.style_id)} data-testid={`effects-style-${s.style_id}`}
                className="w-full grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-3 text-left hover:bg-muted/40 transition-colors">
                <span className="text-sm">{s.name}{s.remixes.length ? <span className="ml-2 text-[10px] font-osd text-primary">{s.remixes.length} REMIX</span> : null}</span>
                <span className="font-osd text-xs text-muted-foreground">♥ {s.likes}</span>
                <span className="font-osd text-xs text-muted-foreground">⤓ {s.downloads}</span>
                <span className="font-osd text-xs text-muted-foreground">skip {s.skips}</span>
                <span className={`font-osd text-xs ${sc(s.score)}`}>{s.score > 0 ? "+" : ""}{s.score}</span>
              </button>
              {open === s.style_id && (
                <div className="px-4 pb-4 flex flex-col gap-3" data-testid={`effects-style-detail-${s.style_id}`}>
                  <div className="flex flex-wrap gap-1.5">
                    {s.apps.map((a) => <span key={a.appearance} className="px-2 py-1 text-[11px] border border-border">{APP_FR[a.appearance] || a.appearance} <b className={sc(a.score)}>{a.score > 0 ? "+" : ""}{a.score}</b> <span className="text-muted-foreground">({a.n})</span></span>)}
                  </div>
                  {s.tags.length ? (
                    <table className="w-full text-xs"><thead><tr className="text-muted-foreground font-osd text-[10px]"><th className="text-left py-1">EFFET</th><th>♥</th><th>⤓</th><th>👎</th><th>skip</th><th className="text-right">SCORE</th></tr></thead>
                      <tbody>{s.tags.map((t) => <tr key={t.tag} className="border-t border-border/60"><td className="py-1.5" title={t.tag}>{t.label}</td><td className="text-center">{t.likes}</td><td className="text-center">{t.downloads}</td><td className="text-center">{t.unlikes}</td><td className="text-center">{t.skips}</td><td className={`text-right font-osd ${sc(t.score)}`}>{t.score > 0 ? "+" : ""}{t.score}</td></tr>)}</tbody></table>
                  ) : <p className="text-xs text-muted-foreground">Aucun effet aléatoire noté sur ce style pour l'instant.</p>}
                  {s.remixes.length ? (
                    <div className="flex flex-wrap gap-2">{s.remixes.map((r) => (
                      <button key={r.id} type="button" onClick={() => toggle(r.id)} data-testid={`effects-remix-toggle-${r.id}`} title={r.tags.join(", ")}
                        className={`px-2.5 py-1 text-[11px] border transition-colors ${r.active ? "border-primary text-primary" : "border-border text-muted-foreground line-through"}`}>{r.name}</button>
                    ))}</div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
          {!data.styles.length && <p className="p-4 text-sm text-muted-foreground">Aucun geste enregistré pour l'instant.</p>}
        </div>
      </section>
    </div>
  );
};
